'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import NextImage from 'next/image';
import NextLink from 'next/link';
import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {useAfterFirstPaint} from '../../hooks';
import {RichContent} from '../article';
import {ListingEmptyState, type ListingEmptyStateProps} from '../listing/ListingEmptyState';
import {Button, Section, Typography, VisuallyHidden} from '../primitives';
import {HighlightCard} from './HighlightCard';

const SIZES_HALF = '(max-width: 900px) 50vw, 290px';

export interface SpotlightMediaItem {
  src: string;
  alt?: string;
}

export interface SpotlightHighlight {
  label: string;
  displayValue: string;
}

export interface SpotlightSectionProps {
  label: string;
  title: string;
  description: string;
  media: SpotlightMediaItem[];
  highlights: SpotlightHighlight[];
  buttonText?: string;
  viewDetailsHref?: string;
  actions?: ReactNode;
  onViewDetailsClick?: () => void;
  empty?: ListingEmptyStateProps;
}

const SpotlightRow = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(5),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(22),
  },
}));

const SpotlightInfo = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.25),
  flex: 1,
  minWidth: 0,
}));

const SpotlightLabel = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
}));

const SpotlightDot = styled('span')(({theme}) => ({
  width: 10,
  height: 10,
  flexShrink: 0,
  borderRadius: theme.radii.full,
  background: theme.palette.colors.surface.primary.default,
}));

const HighlightsGrid = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(1),
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 176px))',
  },
}));

const SpotlightActions = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.75),
  '& > *': {flex: 1},
  [theme.breakpoints.up('md')]: {
    '& > *': {flex: '0 0 auto'},
  },
}));

const SpotlightMedia = styled(Box)(({theme}) => ({
  order: -1,
  flex: 1,
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridAutoRows: '1fr',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {order: 0, minHeight: 476},
}));

const CollageTile = styled(Box)(({theme}) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.radii.images,
  boxShadow: theme.customShadows.card,
  minHeight: 200,
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
}));

function SpotlightCollage({images, alt}: {images: SpotlightMediaItem[]; alt: string}) {
  const topLeft = images.at(0);
  const bottomLeft = images.at(1);
  const right = images.at(2);
  const showImages = useAfterFirstPaint();
  return (
    <SpotlightMedia>
      {topLeft && (
        <CollageTile sx={{gridColumn: 1, gridRow: 1}}>
          {showImages && <NextImage src={topLeft.src} alt={topLeft.alt ?? alt} fill sizes={SIZES_HALF} style={{objectFit: 'cover'}} />}
        </CollageTile>
      )}
      {bottomLeft && (
        <CollageTile sx={{gridColumn: 1, gridRow: 2}}>
          {showImages && (
            <NextImage src={bottomLeft.src} alt={bottomLeft.alt ?? alt} fill sizes={SIZES_HALF} style={{objectFit: 'cover'}} />
          )}
        </CollageTile>
      )}
      {right && (
        <CollageTile sx={{gridColumn: 2, gridRow: '1 / span 2'}}>
          {showImages && <NextImage src={right.src} alt={right.alt ?? alt} fill sizes={SIZES_HALF} style={{objectFit: 'cover'}} />}
        </CollageTile>
      )}
    </SpotlightMedia>
  );
}

export function SpotlightSection({
  label,
  title,
  description,
  media,
  highlights,
  buttonText,
  viewDetailsHref,
  actions,
  onViewDetailsClick,
  empty,
}: SpotlightSectionProps) {
  const {t} = useTranslation();
  if ((media.length === 0 || !viewDetailsHref) && empty) {
    return (
      <Section tone="raised">
        <Container>
          <ListingEmptyState {...empty} card />
        </Container>
      </Section>
    );
  }
  return (
    <Section tone="raised">
      <Container>
        <SpotlightRow>
          <SpotlightInfo>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
              <SpotlightLabel>
                <SpotlightDot aria-hidden />
                <Typography
                  size="label"
                  weight="regular"
                  lineHeight="20"
                  as="p"
                  sx={{textTransform: 'uppercase', letterSpacing: '2.6px', color: theme => theme.palette.colors.text.primary.default}}
                >
                  {label}
                </Typography>
              </SpotlightLabel>
              <Typography
                size="title-2"
                weight="bold"
                mobileSize="title-4"
                mobileWeight="bold"
                as="h2"
                sx={{textTransform: 'capitalize', color: theme => theme.palette.colors.text.neutral.strong}}
              >
                {title}
              </Typography>
              <RichContent html={description} collapsible={false} sx={{color: theme => theme.palette.colors.text.neutral.default}} />
            </Box>

            {highlights.length > 0 && (
              <HighlightsGrid sx={highlights.length === 3 ? {gridTemplateColumns: {md: 'repeat(3, minmax(0, 176px))'}} : undefined}>
                {highlights.map((highlight, index) => (
                  <HighlightCard key={`${highlight.label}-${index}`} label={highlight.label} value={highlight.displayValue} />
                ))}
              </HighlightsGrid>
            )}

            <SpotlightActions>
              {actions}
              {viewDetailsHref && (
                <Button variant="outlined-dark" component={NextLink} href={viewDetailsHref} onClick={onViewDetailsClick}>
                  {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                  {buttonText || t('view_details')}
                  <VisuallyHidden> {title}</VisuallyHidden>
                </Button>
              )}
            </SpotlightActions>
          </SpotlightInfo>

          <SpotlightCollage images={media} alt={title} />
        </SpotlightRow>
      </Container>
    </Section>
  );
}

SpotlightSection.displayName = 'SpotlightSection';
